import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { DatePicker, Skeleton, Empty } from 'antd';
import Chart from '@components/charts/Chart';
import { apiGetConsumptionByBranchByDay } from '@services/ManageDevices/DeviceServices';
import { showError } from '@components/notification';
import { formatCurrency } from '@utils/utils';
import dayjs from 'dayjs';
import { abbreviateBranch, barOptions, PALETTE } from '../dashboardTheme';

const ConsumptionChart = forwardRef((props, ref) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs().subtract(1, 'day'));

  useImperativeHandle(ref, () => ({
    refetch: () => {
      fetchData(selectedDate);
    }
  }));

  const fetchData = async (date) => {
    setLoading(true);
    try {
      const res = await apiGetConsumptionByBranchByDay({
        date: dayjs(date).format('YYYY-MM-DD')
      });
      const branches = res.data?.data?.branches;
      setData(Array.isArray(branches) ? branches : []);
    } catch (error) {
      console.error('Error fetching consumption data:', error);
      showError('Không thể tải dữ liệu tiêu thụ');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedDate);
  }, [selectedDate]);

  const categories = data.map((item) => abbreviateBranch(item.branch_name) || item.branch_code);
  const seriesData = data.map((item) => parseFloat(item.consumption || 0) / 1000);
  const totalConsumption = seriesData.reduce((sum, value) => sum + value, 0);
  const topIndex = seriesData.indexOf(Math.max(...seriesData));

  return (
    <div>
      <div className="chart-toolbar">
        <div className="chart-metric">
          <span className="chart-metric__label">Tổng tiêu thụ</span>
          <span className="chart-metric__value">
            {formatCurrency(totalConsumption)}
            <small>m³</small>
          </span>
          {data.length > 0 && topIndex >= 0 && (
            <span className="chart-metric__hint">
              Cao nhất: <b>{categories[topIndex]}</b> · {formatCurrency(seriesData[topIndex])} m³
            </span>
          )}
        </div>
        <DatePicker
          value={selectedDate}
          onChange={(date) => date && setSelectedDate(date)}
          format="DD/MM/YYYY"
          placeholder="Chọn ngày"
          allowClear={false}
          disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
        />
      </div>
      {loading ? (
        <Skeleton active paragraph={{ rows: 7 }} />
      ) : data.length === 0 ? (
        <Empty className="chart-empty" description="Chưa có dữ liệu cho ngày này" />
      ) : (
        <div className="chart-container">
          <Chart
            options={barOptions({
              colors: [PALETTE.primary],
              fill: {
                type: 'gradient',
                gradient: {
                  shade: 'light',
                  type: 'vertical',
                  gradientToColors: [PALETTE.cyan],
                  opacityFrom: 1,
                  opacityTo: 0.9,
                  stops: [0, 100]
                }
              },
              plotOptions: { bar: { dataLabels: { position: 'top' } } },
              dataLabels: {
                enabled: true,
                offsetY: -20,
                style: { fontSize: '11px', fontWeight: 600, colors: [PALETTE.muted] },
                formatter: (val) => formatCurrency(val)
              },
              xaxis: { categories },
              yaxis: {
                labels: { formatter: (val) => formatCurrency(Math.round(val)) }
              },
              tooltip: { y: { formatter: (val) => `${formatCurrency(val, 2)} m³` } },
              legend: { show: false }
            })}
            type="bar"
            series={[{ name: 'Tiêu thụ', data: seriesData }]}
            height={380}
          />
        </div>
      )}
    </div>
  );
});

export default ConsumptionChart;
